import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { peopleSelectControlSx, peopleSelectMenuProps, peopleSelectSx } from "../../People/peopleSelectStyles";
import { fieldErrorSx, peopleFormErrorTextSx } from "../../People/peopleFormValidation";
import { mapCannedItemToForm, extractCannedGroupIds } from "./cannedUtils";

const CANNED_GROUP_MENU_HEIGHT = 124;

const cannedGroupSelectMenuProps = {
  ...peopleSelectMenuProps,
  disableScrollLock: true,
  PaperProps: {
    ...peopleSelectMenuProps.PaperProps,
    sx: {
      ...peopleSelectMenuProps.PaperProps.sx,
      maxHeight: CANNED_GROUP_MENU_HEIGHT,
      overflow: "hidden",
      boxSizing: "border-box",
    },
  },
  MenuListProps: {
    sx: {
      maxHeight: CANNED_GROUP_MENU_HEIGHT,
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(0,0,0,0.2) transparent",
      py: 0.5,
    },
  },
};

const fieldLabelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  mb: 0.75,
};

const singleLineInputSx = {
  width: 360,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    background: "#FFF",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    fontFamily: "Inter",
    fontSize: 14,
    "& fieldset": { border: "1px solid #D5DCE1" },
    "&:hover fieldset": { border: "1px solid #D5DCE1" },
    "&.Mui-focused fieldset": { border: "1px solid #D5DCE1" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 1.5,
    boxSizing: "border-box",
    height: 40,
    "&::placeholder": { color: "#C5CED6", opacity: 1 },
  },
};

const textareaSx = {
  width: 360,
  maxWidth: "100%",
  height: 120,
  padding: "8px 12px",
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  background: "#FFF",
  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
  color: "#061522",
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  boxSizing: "border-box",
  resize: "none",
  overflowY: "auto",
  overflowX: "hidden",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  display: "block",
  "&::placeholder": {
    color: "#C5CED6",
    opacity: 1,
  },
  "&:focus": {
    outline: "none",
    borderColor: "#148DFD",
  },
};

const checkboxSx = {
  p: 0,
  width: 16,
  height: 16,
  flexShrink: 0,
  "&.MuiCheckbox-root": {
    width: 16,
    height: 16,
  },
  "& .MuiSvgIcon-root": {
    width: 16,
    height: 16,
    fontSize: 16,
  },
};

function CheckboxUnchecked() {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "4px",
        border: "1px solid #D5DCE1",
        background: "#FFF",
        boxSizing: "border-box",
      }}
    />
  );
}

function CheckboxChecked() {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "4px",
        border: "1px solid #148DFD",
        background: "#FFF",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path
          d="M6.5 0.5L2.74922 4.25078L0.5 2.00031"
          stroke="#148DFD"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

const selectSx = peopleSelectControlSx({ width: 360, maxWidth: "100%" });

const EMPTY_FORM = {
  title: "",
  shortcut: "",
  content: "",
  groupIds: [],
  isPrivate: false,
};
const EMPTY_ERRORS = { title: "", shortcut: "", content: "", groups: "" };

export default function CannedCreateModal({
  open,
  onClose,
  onConfirm,
  groupOptions = [],
  optionsLoading = false,
  submitting = false,
  defaultGroupId = "",
  editingItem = null,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const isEditMode = Boolean(editingItem?._id);
  const fallbackGroupIds = editingItem
    ? extractCannedGroupIds(editingItem)
    : defaultGroupId
      ? [defaultGroupId]
      : [];

  const selectedGroupLabels = (form.groupIds || [])
    .map((id) => groupOptions.find((g) => g.id === id)?.label)
    .filter(Boolean);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors(EMPTY_ERRORS);
      return;
    }

    if (editingItem) {
      setForm(mapCannedItemToForm(editingItem));
    } else {
      setForm({
        ...EMPTY_FORM,
        groupIds: defaultGroupId ? [defaultGroupId] : [],
      });
    }
    setErrors(EMPTY_ERRORS);
  }, [open, defaultGroupId, editingItem]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = { title: "", shortcut: "", content: "", groups: "" };
    let valid = true;

    if (!form.title.trim()) {
      next.title = "Title is required.";
      valid = false;
    }
    if (!form.shortcut.trim()) {
      next.shortcut = "Shortcut is required.";
      valid = false;
    }
    if (!form.content.trim()) {
      next.content = "Response content is required.";
      valid = false;
    }
    if (!form.isPrivate && !(form.groupIds || []).length) {
      next.groups = "Select at least one group.";
      valid = false;
    }
    if (form.isPrivate && !(form.groupIds || []).length && !defaultGroupId) {
      next.groups = "Select at least one group.";
      valid = false;
    }

    setErrors(next);
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const resolvedGroupIds =
      (form.groupIds || []).length > 0
        ? form.groupIds
        : defaultGroupId
          ? [defaultGroupId]
          : [];
    onConfirm({
      id: editingItem?._id,
      title: form.title.trim(),
      shortcut: form.shortcut.trim(),
      content: form.content.trim(),
      groupIds: resolvedGroupIds,
      isPrivate: form.isPrivate,
    });
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          height: 584,
          maxWidth: "95vw",
          maxHeight: "95vh",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
          overflow: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          p: "24px",
          position: "relative",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2.5,
          }}
        >
          {isEditMode ? "Edit Canned Response" : "Create Canned Response"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          <Box>
            <Typography sx={fieldLabelSx}>Title</Typography>
            <TextField
              placeholder="e.g. welcome"
              value={form.title}
              onChange={set("title")}
              error={Boolean(errors.title)}
              sx={{ ...singleLineInputSx, ...fieldErrorSx(errors.title) }}
            />
            {errors.title ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.title}</Typography>
            ) : null}
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Shortcut</Typography>
            <TextField
              placeholder="e.g. / welcome"
              value={form.shortcut}
              onChange={set("shortcut")}
              error={Boolean(errors.shortcut)}
              sx={{ ...singleLineInputSx, ...fieldErrorSx(errors.shortcut) }}
            />
            {errors.shortcut ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.shortcut}</Typography>
            ) : null}
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Response Content</Typography>
            <Box
              component="textarea"
              placeholder="Enter your response"
              value={form.content}
              onChange={set("content")}
              sx={{
                ...textareaSx,
                ...(errors.content ? { borderColor: "#FF4D4F" } : {}),
              }}
            />
            {errors.content ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.content}</Typography>
            ) : null}
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Groups</Typography>
            <FormControl
              size="small"
              sx={selectSx}
              disabled={optionsLoading || form.isPrivate}
            >
              <Select
                multiple
                displayEmpty
                value={form.groupIds || []}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    groupIds: typeof value === "string" ? value.split(",") : value,
                  }));
                  setErrors((prev) => ({ ...prev, groups: "" }));
                }}
                IconComponent={Dropdown}
                MenuProps={cannedGroupSelectMenuProps}
                sx={peopleSelectSx}
                error={Boolean(errors.groups)}
                renderValue={() => (
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      color: form.isPrivate
                        ? "#C5CED6"
                        : selectedGroupLabels.length
                          ? "#061522"
                          : "#C5CED6",
                      fontSize: 14,
                      fontFamily: "Inter",
                      fontWeight: 400,
                    }}
                  >
                    {form.isPrivate
                      ? selectedGroupLabels.join(", ") || "Current group"
                      : selectedGroupLabels.length
                        ? selectedGroupLabels.join(", ")
                        : "Select"}
                  </Typography>
                )}
              >
                {groupOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                    <Checkbox
                      checked={(form.groupIds || []).includes(opt.id)}
                      icon={<CheckboxUnchecked />}
                      checkedIcon={<CheckboxChecked />}
                      disableRipple
                      sx={{ ...checkboxSx, mr: 1 }}
                    />
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.groups ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.groups}</Typography>
            ) : null}
          </Box>

          <FormControlLabel
            sx={{ alignItems: "flex-start", m: 0, gap: "8px" }}
            control={
              <Checkbox
                checked={form.isPrivate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    isPrivate: checked,
                    groupIds:
                      (prev.groupIds || []).length > 0
                        ? prev.groupIds
                        : fallbackGroupIds.length
                          ? fallbackGroupIds
                          : defaultGroupId
                            ? [defaultGroupId]
                            : [],
                  }));
                }}
                icon={<CheckboxUnchecked />}
                checkedIcon={<CheckboxChecked />}
                disableRipple
                sx={checkboxSx}
              />
            }
            label={
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: 14,
                  lineHeight: "18px",
                  pt: "0px",
                }}
              >
                Make this response private (visible only to me)
              </Typography>
            }
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: "auto", pt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || optionsLoading}
            variant="contained"
            disableElevation
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              bgcolor: "#148DFD",
              backgroundColor: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#127EDE",
                backgroundColor: "#127EDE",
                boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                backgroundColor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {submitting
              ? isEditMode
                ? "Saving…"
                : "Creating…"
              : isEditMode
                ? "Save Changes"
                : "Create Response"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
